/**
 * What each person may use. Permissions are given per person on the Team
 * screen and combine freely. The database enforces the same rules
 * (supabase/migrations/20261024170000_add_team_permissions.sql); this module
 * only decides what the app shows, so hiding something here is never the
 * security.
 */

export type AppPermission = 'events' | 'outreach' | 'check_in' | 'reports' | 'team'

export const PERMISSIONS: ReadonlyArray<Readonly<{ permission: AppPermission; label: string; summary: string }>> = [
  { permission: 'events', label: 'Events', summary: 'Create and edit events, forms and sessions' },
  { permission: 'outreach', label: 'Outreach', summary: 'Join sessions and register people' },
  { permission: 'check_in', label: 'Check-in', summary: 'Check people in, including the Box Office' },
  { permission: 'reports', label: 'Reports', summary: 'Dashboard and exports' },
  // Stored as `team` (app_permission, member_permissions) but shown as Permissions:
  // it is the Permissions tab. The stored name is kept so
  // existing grants and older app versions keep working.
  { permission: 'team', label: 'Permissions', summary: 'Give out these permissions' },
]

/** One-tap starting points on the Manage permissions screen. They only tick boxes; nothing else is stored. */
export const PRESETS: ReadonlyArray<Readonly<{ label: string; permissions: readonly AppPermission[] }>> = [
  { label: 'Outreacher', permissions: ['outreach', 'check_in'] },
  { label: 'Check-in', permissions: ['check_in'] },
  { label: 'Lead', permissions: ['events', 'outreach', 'check_in', 'reports'] },
  { label: 'Pastor', permissions: ['reports'] },
  { label: 'Admin', permissions: ['events', 'outreach', 'check_in', 'reports', 'team'] },
]

const known = new Set<string>(PERMISSIONS.map((item) => item.permission))

export function isAppPermission(value: unknown): value is AppPermission {
  return typeof value === 'string' && known.has(value)
}

/** Keeps only known permissions, in the order the Manage permissions screen lists them. */
export function toPermissions(value: unknown): AppPermission[] {
  if (!Array.isArray(value)) return []
  const given = new Set(value.filter(isAppPermission))
  return PERMISSIONS.map((item) => item.permission).filter((permission) => given.has(permission))
}

export function permissionLabel(permission: AppPermission): string {
  return PERMISSIONS.find((item) => item.permission === permission)?.label ?? permission
}

/** The preset a set of permissions matches exactly, if any. */
export function presetFor(permissions: readonly AppPermission[]): string | null {
  const key = [...permissions].sort().join(',')
  return PRESETS.find((preset) => [...preset.permissions].sort().join(',') === key)?.label ?? null
}

type Has = (permission: AppPermission) => boolean

/**
 * Where someone lands when they open the app: the first tab they can use,
 * reading the bottom bar left to right. With no tabs at all it's Settings,
 * which says they don't have access yet and lets them sign out.
 */
export function homePath(permissions: readonly AppPermission[]): string {
  const has: Has = (permission) => permissions.includes(permission)
  if (has('events')) return '/events'
  if (has('outreach')) return '/sessions'
  if (has('check_in')) return '/check-in'
  if (has('reports')) return '/reports'
  if (has('team')) return '/team'
  return '/settings'
}

/**
 * Whether someone may open an address inside the signed-in app. Anything not
 * listed (Help, Release notes, account pages) is open to everyone.
 */
export function canOpen(permissions: readonly AppPermission[], pathname: string): boolean {
  const has: Has = (permission) => permissions.includes(permission)
  const path = pathname.replace(/\/+$/, '') || '/'
  const under = (root: string) => path === root || path.startsWith(`${root}/`)
  if (under('/team')) return has('team')
  if (under('/reports')) return has('reports')
  if (path === '/check-in') return has('check_in')
  if (under('/sessions')) return has('outreach')
  // My contacts is part of the Outreach area.
  if (under('/contacts')) return has('outreach')
  if (under('/events')) return has('events')
  return true
}
